import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import type { CompetitionPublic, Result } from '../../types/results';
import type { BoardSortMode, Team } from '../../types/domain';
import { Logo } from '../Logo';
import { formatDateCz } from '../../utils/dateFormat';
import { logoScale } from '../../utils/logoScale';
import { bestAttemptPerTeam } from '../results/bestAttempt';

export { bestAttemptPerTeam };

export const MEDAL_COLORS: Record<number, { bg: string; fg: string }> = {
  1: { bg: '#FFD54F', fg: '#2d1f00' },
  2: { bg: '#CFD8DC', fg: '#1a1a1a' },
  3: { bg: '#D4A373', fg: '#FFFFFF' },
};

export const sortByRank = (results: Result[]): Result[] =>
  [...results].sort((a, b) => {
    if (a.rank == null && b.rank == null) return 0;
    if (a.rank == null) return 1;
    if (b.rank == null) return -1;
    return a.rank - b.rank;
  });

export const sortByStartNumber = (results: Result[]): Result[] =>
  [...results].sort((a, b) => a.teamStartNumber - b.teamStartNumber);

/**
 * Synteticky placeholder "result" pro tymy bez vysledku — pouziva se v radcich boardu
 * kdyz je aktivni boardShowAllTeams. Negativni id zajistuje unikatnost proti realnym Resultum.
 */
export interface BoardRow extends Result {
  __missing?: boolean;
}

const missingRow = (disciplineId: number, team: Team): BoardRow => ({
  id: -(disciplineId * 1_000_000 + team.id),
  teamId: team.id,
  teamName: team.name,
  teamStartNumber: team.startNumber,
  disciplineId,
  disciplineName: '',
  attemptNumber: 1,
  timeFinalMs: null,
  timeFinalFormatted: '—',
  isNp: false,
  penaltySeconds: 0,
  penaltyLane: null,
  rank: null,
  note: null,
  attempts: [],
  createdAt: '',
  updatedAt: '',
  __missing: true,
});

/**
 * Postavi seznam radku pro board: serazene vysledky (+ pripadne i tymy bez vysledku jako placeholder).
 * @param topN Limit pro "top" rezim (pouziva se jen kdyz showAll=false).
 */
export const buildBoardRows = (
  results: Result[],
  allTeams: Team[] | undefined,
  categoryId: number,
  disciplineId: number,
  opts: { showAll: boolean; sortMode: BoardSortMode; topN: number },
): BoardRow[] => {
  // Dedupe: jen nejlepsi pokus per druzstvo (vyhnuti se duplicitam u disciplin s vice pokusy)
  const deduped = bestAttemptPerTeam(results);

  if (!opts.showAll || !allTeams) {
    const sorted =
      opts.sortMode === 'START_NUMBER' ? sortByStartNumber(deduped) : sortByRank(deduped);
    return opts.showAll ? sorted : sorted.slice(0, opts.topN);
  }

  const teamsInCat = allTeams.filter((t) => t.categoryId === categoryId);
  const resultTeamIds = new Set(results.map((r) => r.teamId));
  const missing = teamsInCat
    .filter((t) => !resultTeamIds.has(t.id))
    .map((t) => missingRow(disciplineId, t));

  // Pro START_NUMBER: sloucit realne vysledky + chybejici a seradit vse podle start cisla dohromady
  // (jinak by chybejici tymy konceptuální, ne na konci).
  // Pro RANK: realne vysledky podle ranku, chybejici na konci podle start cisla.
  if (opts.sortMode === 'START_NUMBER') {
    return [...deduped, ...missing].sort((a, b) => a.teamStartNumber - b.teamStartNumber);
  }
  const sortedByRank = sortByRank(deduped);
  const missingSorted = [...missing].sort((a, b) => a.teamStartNumber - b.teamStartNumber);
  return [...sortedByRank, ...missingSorted];
};

interface BoardHeaderProps {
  competition: CompetitionPublic;
  organizerLogoSrc: string | null;
  /** Pokud zadáno, vykreslí se malé QR vedle názvu soutěže (pro Focus/Carousel/Split layouty). */
  inlineQrShortCode?: string;
}

export const BoardHeader = ({ competition, organizerLogoSrc, inlineQrShortCode }: BoardHeaderProps) => {
  const qrSize = 140;
  const qrSrcSize = qrSize * 2;
  const s = logoScale(competition.publicOrganizerLogoScale);
  return (
    <Stack direction="row" spacing={2.5} alignItems="center">
      <Logo size={80} />
      {organizerLogoSrc && (
        <Box
          component="img"
          src={organizerLogoSrc}
          alt="Logo pořadatele"
          sx={{ height: 80 * s, maxWidth: 280 * s, objectFit: 'contain' }}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{ lineHeight: 1.1, textTransform: 'uppercase', letterSpacing: '0.01em', color: 'text.primary' }}
        >
          {competition.name}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {formatDateCz(competition.competitionDate)}
          {competition.location ? ` • ${competition.location}` : ''}
          {competition.unitName ? ` • Pořadatel: ${competition.unitName}` : ''}
        </Typography>
      </Box>
      {inlineQrShortCode && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0 }}>
          <Box
            component="img"
            src={`/api/public/competitions/${inlineQrShortCode}/qr?size=${qrSrcSize}`}
            alt={`QR ${inlineQrShortCode}`}
            sx={{
              width: qrSize,
              height: qrSize,
              borderRadius: 1.5,
              bgcolor: '#FFFFFF',
              p: 1,
              display: 'block',
            }}
          />
          <Stack spacing={0.25} alignItems="flex-start">
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1rem',
                letterSpacing: '0.14em',
                color: 'text.secondary',
                textTransform: 'uppercase',
                lineHeight: 1.1,
              }}
            >
              Živé výsledky
            </Typography>
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontWeight: 900,
                fontSize: '1.75rem',
                color: 'primary.main',
                letterSpacing: '0.16em',
                lineHeight: 1.1,
              }}
            >
              {inlineQrShortCode}
            </Typography>
          </Stack>
        </Stack>
      )}
      {competition.status === 'LIVE' && (
        <Chip
          label="● LIVE"
          color="error"
          sx={{
            fontWeight: 800,
            letterSpacing: '0.1em',
            fontSize: '1rem',
            px: 2,
            animation: 'boardPulse 2s infinite',
            '@keyframes boardPulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.55 },
            },
          }}
        />
      )}
      {competition.status === 'FINISHED' && <Chip label="DOKONČENO" color="success" />}
    </Stack>
  );
};

interface QrPanelProps {
  shortCode: string;
  publicUrl: string;
  size?: 'lg' | 'sm';
}

/** Velký vpravo v sidebaru (Matrix), malý v rohu (Focus/Carousel/Split). */
export const QrPanel = ({ shortCode, publicUrl, size = 'lg' }: QrPanelProps) => {
  const qrPx = size === 'lg' ? 240 : 120;
  const url = `/api/public/competitions/${shortCode}/qr?size=${qrPx * 2}`;
  if (size === 'sm') {
    return (
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          bgcolor: 'background.paper',
          zIndex: 10,
        }}
      >
        <Box
          component="img"
          src={url}
          alt={`QR ${shortCode}`}
          sx={{ width: qrPx, height: qrPx, borderRadius: 0.5, display: 'block' }}
        />
        <Typography
          sx={{
            mt: 0.5,
            fontFamily: 'monospace',
            fontWeight: 800,
            fontSize: '0.9rem',
            color: 'primary.main',
            letterSpacing: '0.12em',
          }}
        >
          {shortCode}
        </Typography>
      </Paper>
    );
  }
  return (
    <Paper
      elevation={2}
      sx={{
        width: 320,
        flexShrink: 0,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.12em', color: 'text.secondary' }}>
        Živé výsledky
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Naskenujte QR kód a sledujte na svém telefonu
      </Typography>
      <Box component="img" src={url} alt={`QR ${shortCode}`} sx={{ width: 240, height: 240, borderRadius: 1 }} />
      <Typography
        sx={{
          mt: 2,
          fontFamily: 'monospace',
          fontSize: '1.5rem',
          fontWeight: 800,
          color: 'primary.main',
          letterSpacing: '0.15em',
        }}
      >
        {shortCode}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
        {publicUrl}
      </Typography>
    </Paper>
  );
};
